/**
 * Small bespoke inline icons for the live-agent handoff recovery flow
 * (handoff-queue-card.tsx, handoff-timeout-options.tsx,
 * handoff-timeout-message.tsx). Hand-written SVGs — not lucide-react —
 * because the design spec calls out literal path data to match exactly
 * (see each icon's comment for the source). `stroke="currentColor"` so the
 * color comes from the wrapping StandardCard tile (see
 * primitives/standard-card.tsx's TONE_ICON_COLOR) rather than being
 * hardcoded here — both consumers happen to want exactly the tone color
 * StandardCard already applies for their respective tones.
 */
import type { SVGProps } from 'react';

type IconProps = Omit<SVGProps<SVGSVGElement>, 'viewBox' | 'children'> & {
  size?: number;
};

/** Figma spec: `<circle cx=12 cy=12 r=9/><path d="M12 7v5l3 2"/>` — used by
 *  HandoffQueueCard (WAITING) and HandoffTimeoutOptions (TIMED_OUT). */
export function ClockIcon({
  size = 16,
  fill = 'none',
  stroke = 'currentColor',
  strokeWidth = 2,
  strokeLinecap = 'round',
  strokeLinejoin = 'round',
  className,
  ...props
}: IconProps) {
  return (
    <svg
      aria-hidden="true"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={fill}
      stroke={stroke}
      strokeWidth={strokeWidth}
      strokeLinecap={strokeLinecap}
      strokeLinejoin={strokeLinejoin}
      className={className}
      {...props}
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}

/** Figma spec: `<path d="M4 5h16v11H7l-3 3z"/>` — HandoffTimeoutMessage's
 *  (leave-a-message view) tile icon. */
export function SpeechBubbleIcon({
  size = 16,
  fill = 'none',
  stroke = 'currentColor',
  strokeWidth = 2,
  strokeLinecap = 'round',
  strokeLinejoin = 'round',
  className,
  ...props
}: IconProps) {
  return (
    <svg
      aria-hidden="true"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={fill}
      stroke={stroke}
      strokeWidth={strokeWidth}
      strokeLinecap={strokeLinecap}
      strokeLinejoin={strokeLinejoin}
      className={className}
      {...props}
    >
      <path d="M4 5h16v11H7l-3 3z" />
    </svg>
  );
}
